import { useState } from "react";
import { usePageMeta } from "@/lib/seo";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const MODULES = ["shop-advisor", "the-stipulator", "dealer-advisor", "lead-advisor", "marketing-advisor", "contractor-advisor", "tech-advisors"];
const BIZ_TYPES = ["auto_repair", "collision", "dealership", "contractor", "service", "other"];

export default function DemoPage() {
  usePageMeta("Book a Demo", "See The ShopAdvisor™ in action — book a live demo for your auto repair shop.");
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get("module") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", business_type: "", interested_modules: preselected ? [preselected] : [] });
  const [loading, setLoading] = useState(false);

  const toggleModule = (mod) => {
    setForm(prev => ({ ...prev, interested_modules: prev.interested_modules.includes(mod) ? prev.interested_modules.filter(m => m !== mod) : [...prev.interested_modules, mod] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/api/demo-requests", form);
      toast.success("Demo request submitted! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", business_name: "", business_type: "", interested_modules: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20 px-6 md:px-12 lg:px-24" data-testid="demo-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-3">Book a Demo</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-black text-slate-900 mb-4">See ShopAdvisor in Action</h1>
          <p className="text-slate-500">Get a personalized walkthrough tailored to your business.</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm space-y-5" data-testid="demo-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Full Name</Label><Input className="bg-white border-slate-300 text-slate-900" placeholder="John Smith" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="demo-name-input" /></div>
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Email</Label><Input type="email" className="bg-white border-slate-300 text-slate-900" placeholder="john@shop.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="demo-email-input" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Phone</Label><Input className="bg-white border-slate-300 text-slate-900" placeholder="(555) 000-0000" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required data-testid="demo-phone-input" /></div>
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Business Name</Label><Input className="bg-white border-slate-300 text-slate-900" placeholder="Smith's Auto" value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} required data-testid="demo-business-input" /></div>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-1.5 block">Business Type</Label>
            <Select value={form.business_type} onValueChange={v => setForm({...form, business_type: v})}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="demo-biztype-select"><SelectValue placeholder="Select your business type" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">{BIZ_TYPES.map(t => <SelectItem key={t} value={t} className="text-slate-900">{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Interested Modules</Label>
            <div className="flex flex-wrap gap-2">
              {MODULES.map(m => (
                <button type="button" key={m} onClick={() => toggleModule(m)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.interested_modules.includes(m) ? "bg-blue-600 border-blue-500 text-white" : "border-slate-300 text-slate-600 hover:border-blue-400"}`}
                  data-testid={`demo-module-${m}`}>{m.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50" data-testid="demo-submit-btn">
            {loading ? "Submitting..." : "Request Demo"}
          </button>
        </form>
      </div>
    </div>
  );
}
